<template>
  <div
    ref="accordionElm"
    class="accordion"
    :class="[{ 'accordion--open': isOpen }, customClass]"
  >
    <div
      class="accordion__header"
      :aria-expanded="isOpen"
      :aria-controls="toggleId + '-content'"
      @click="isOpen = !isOpen"
    >
      <slot name="trigger"></slot>
    </div>
    <transition name="expand" appear>
      <div
        v-show="isOpen"
        :id="toggleId + '-content'"
        ref="content"
        :aria-labelledby="toggleId"
        role="region"
        class="accordion__content"
      >
        <slot name="content"></slot>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { onMounted, watch, ref, inject } from 'vue';

const toggleId = Math.random().toString(36).substring(2, 12);

const props = defineProps({
  customClass: {
    type: String,
    default: '',
  },
  title: {
    type: undefined,
    default: '<div class="p-v--12"><p class="text--medium">Title</p></div>',
  },
  chevron: {
    type: undefined,
    default:
      '<i class="icon icon-chevrondown icon--sm" aria-hidden="true"></i>',
  },
  openOnLoad: {
    type: Boolean,
  },
  scollYBuffer: {
    type: Number,
    default: 10,
  },
});

const accordionElm = ref(null);
const isOpen = ref(props.openOnLoad ? true : false);

const groupStates = inject('accordionGroupStates', null);
const updateAccordionItem = inject('updateAccordionItem', null);

const observerHandler = () => {
  // Target the element to observe
  const targetElement = accordionElm.value;

  // Create a new MutationObserver
  const observer = new MutationObserver((mutationsList, observer) => {
    // Loop through the mutations list
    for (const mutation of mutationsList) {
      // Check if the mutation type is an attribute change
      if (mutation.type === 'attributes') {
        // Get the name and new value of the changed attribute
        const attributeName = mutation.attributeName;
        if (attributeName === 'data-force-open') {
          isOpen.value = targetElement.getAttribute('data-force-open') === 'true'
        }
      }
    }
  });

  // Configure the observer to watch for attribute changes
  const config = { attributes: true };

  // Start observing the target element with the specified configuration
  observer.observe(targetElement, config);
};

onMounted(() => {
  if (accordionElm.value.getAttribute('data-force-open')) {
    observerHandler();
  }
  if (updateAccordionItem) {
    updateAccordionItem(accordionElm.value, isOpen.value);
  }
});

watch(isOpen, () => {
  if (updateAccordionItem) {
    updateAccordionItem(accordionElm.value, isOpen.value);
  }
});

if (groupStates) {
  watch(groupStates, () => {
    for (let [k, v] of groupStates.value) {
      if (k === accordionElm.value && v !== isOpen.value) {
        isOpen.value = v;
      }
    }
  });
}
</script>

<style scoped>
.expand-enter-to,
.expand-leave-from {
  transition: all 0.2s;
  max-height: 1000px;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
}
</style>
